import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

type TokenScope = 'read' | 'readwrite';

interface ApiTokenInfo {
  id: number;
  label: string;
  scope: TokenScope;
  canDelete: boolean;
  allowPrefix: string;
  createdAt: number;
  lastUsedAt: number | null;
}

/** 'loading' / 'error' while fetching, otherwise the current token list. */
type ListState = 'loading' | 'error' | ApiTokenInfo[];

const scopeLabel = (s: TokenScope): string => (s === 'read' ? 'Read-only' : 'Read-write');

/** The remote MCP endpoint for the current deployment (protocol + host + /mcp). */
const mcpUrl = (): string =>
  typeof window !== 'undefined' ? `${window.location.origin}/mcp` : '/mcp';

/**
 * Settings → "API access": create, list, and revoke bearer tokens that let a
 * Claude agent reach this vault over the remote MCP endpoint. Session-cookie
 * authenticated; every request is same-origin with credentials.
 */
export function ApiAccess() {
  const [list, setList] = useState<ListState>('loading');
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState<TokenScope>('readwrite');
  const [canDelete, setCanDelete] = useState(true);
  const [allowPrefix, setAllowPrefix] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [created, setCreated] = useState<{ token: string; scope: TokenScope } | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setList('loading');
    try {
      const res = await fetch('/api/tokens', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('load failed');
      const body = (await res.json()) as { tokens?: ApiTokenInfo[] };
      setList(body.tokens ?? []);
    } catch {
      setList('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      const res = await fetch('/api/tokens', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: label.trim() || undefined,
          scope,
          canDelete: scope === 'readwrite' ? canDelete : true,
          allowPrefix: scope === 'readwrite' ? allowPrefix.trim() : '',
        }),
      });
      const body = (await res.json()) as { token?: string; scope?: TokenScope; error?: string };
      if (!res.ok || !body.token) {
        setCreateError(body.error ?? 'Could not create token.');
        return;
      }
      setCreated({ token: body.token, scope: body.scope ?? scope });
      setCopied(false);
      setLabel('');
      setScope('readwrite');
      setCanDelete(true);
      setAllowPrefix('');
    } catch {
      setCreateError('Network error.');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (t: ApiTokenInfo) => {
    if (!window.confirm(`Revoke “${t.label}”? Any app using this token will stop working.`)) return;
    try {
      await fetch(`/api/tokens/${t.id}`, { method: 'DELETE', credentials: 'same-origin' });
    } catch {
      // Ignore: the reload below reflects the server's actual state.
    }
    void load();
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const done = () => {
    setCreated(null);
    setCopied(false);
    void load();
  };

  return (
    <section className="settings-section">
      <h3>API access</h3>
      <p className="sd-hint sd-api-intro">
        Connect a Claude agent to this vault over MCP. Add a custom connector (or a Claude Code
        remote MCP) pointed at <code>{mcpUrl()}</code>, and authenticate with a token below as a{' '}
        <strong>Bearer</strong> credential.
      </p>

      {list === 'loading' && <div className="sd-hint">Loading tokens…</div>}

      {list === 'error' && (
        <div className="sd-api-error-row">
          <span className="sd-error">Couldn’t load tokens.</span>
          <button type="button" className="sd-btn" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {Array.isArray(list) && list.length === 0 && (
        <div className="sd-hint">No tokens yet — create one to connect an agent.</div>
      )}

      {Array.isArray(list) && list.length > 0 && (
        <ul className="sd-token-list">
          {list.map((t) => (
            <li key={t.id} className="sd-token">
              <div className="sd-token-main">
                <span className="sd-token-label">{t.label}</span>
                <span className={`sd-scope-badge${t.scope === 'read' ? ' read' : ''}`}>
                  {scopeLabel(t.scope)}
                </span>
              </div>
              <div className="sd-token-meta">
                Created {dayjs(t.createdAt).fromNow()}
                {' · '}
                {t.lastUsedAt ? `last used ${dayjs(t.lastUsedAt).fromNow()}` : 'never used'}
                {t.scope === 'readwrite' && !t.canDelete && ' · no-delete'}
                {t.allowPrefix && ` · ${t.allowPrefix}/ only`}
              </div>
              <button
                type="button"
                className="sd-btn danger sd-token-revoke"
                onClick={() => void revoke(t)}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}

      {created ? (
        <div className="sd-token-created">
          <div className="sd-success">Token created — copy it now.</div>
          <div className="sd-token-warning">
            This is the only time it will be shown. Store it somewhere safe; you won’t be able to
            view it again.
          </div>
          <div className="sd-token-box">
            <code className="sd-token-value">{created.token}</code>
            <button type="button" className="sd-btn" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="sd-hint">
            Use it as a Bearer token against <code>{mcpUrl()}</code> ({scopeLabel(created.scope)}{' '}
            access).
          </div>
          <button type="button" className="sd-btn primary" onClick={done}>
            Done
          </button>
        </div>
      ) : (
        <form className="sd-token-form" onSubmit={(e) => void create(e)}>
          <div className="settings-field">
            <label htmlFor="sd-token-label">New token label</label>
            <input
              id="sd-token-label"
              value={label}
              placeholder="e.g. Claude Code"
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="settings-field">
            <label htmlFor="sd-token-scope">Access</label>
            <select
              id="sd-token-scope"
              value={scope}
              onChange={(e) => setScope(e.target.value as TokenScope)}
            >
              <option value="readwrite">Read-write</option>
              <option value="read">Read-only</option>
            </select>
          </div>
          {scope === 'readwrite' && (
            <>
              <label className="sd-scope-check">
                <input
                  type="checkbox"
                  checked={canDelete}
                  onChange={(e) => setCanDelete(e.target.checked)}
                />
                Allow deleting notes
              </label>
              <div className="settings-field">
                <label htmlFor="sd-token-prefix">Restrict to folder (optional)</label>
                <input
                  id="sd-token-prefix"
                  value={allowPrefix}
                  placeholder="e.g. Inbox"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  onChange={(e) => setAllowPrefix(e.target.value)}
                />
                <div className="sd-hint">Writes are confined to this folder; leave blank for the whole vault.</div>
              </div>
            </>
          )}
          {createError && <div className="sd-error">{createError}</div>}
          <button type="submit" className="sd-btn primary sd-token-submit" disabled={creating}>
            {creating ? 'Creating…' : 'Create token'}
          </button>
        </form>
      )}

      <AuditActivity />
    </section>
  );
}

interface AuditEvent {
  ts: number;
  surface: string;
  actor: string | null;
  tool: string;
  target: string | null;
  ok: boolean;
  isWrite: boolean;
}

/** Recent agent tool calls (MCP + in-app assistant), read-only. */
function AuditActivity() {
  const [events, setEvents] = useState<AuditEvent[] | 'loading' | 'error'>('loading');

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/audit?limit=25', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('load failed');
      const body = (await res.json()) as { events?: AuditEvent[] };
      setEvents(body.events ?? []);
    } catch {
      setEvents('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="settings-field">
      <label>
        Recent agent activity{' '}
        <button type="button" className="sd-btn sd-audit-refresh" onClick={() => void load()}>
          ↻
        </button>
      </label>
      {events === 'loading' && <div className="sd-hint">Loading…</div>}
      {events === 'error' && <div className="sd-hint">Couldn’t load activity.</div>}
      {Array.isArray(events) && events.length === 0 && (
        <div className="sd-hint">No agent activity yet.</div>
      )}
      {Array.isArray(events) && events.length > 0 && (
        <ul className="sd-email-recent">
          {events.map((e, i) => (
            <li key={i} className={`sd-email-row${e.ok ? '' : ' status-error'}`}>
              <span className={`sd-scope-badge${e.isWrite ? '' : ' read'}`}>{e.tool}</span>
              <span className="sd-email-subj">
                {e.actor ?? e.surface}
                {e.target ? ` · ${e.target}` : ''}
              </span>
              <span className="sd-token-meta">{dayjs(e.ts).fromNow()}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
